import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { siteApi } from '@/api/site';
import { AutopilotResourceEditorPane } from '@/components/autopilot/AutopilotResourceModal.panels';
import {
  buildDraftResourceCode,
  getAssetEditorKind,
  getAssetTransportKind,
  getDefaultAssetSubtype,
  getDraftFilterKind,
  getDraftPrimaryText,
  getDraftSchemaName,
  getDraftValidationError,
  getErrorMessage,
  inferStorageType,
  normalizeDraftToAssetEditor,
  normalizeSubtype,
  type AssetEditorKind,
  type ResourceDraft,
} from '@/components/autopilot/AutopilotResourceModal.helpers';
import {
  getBlueprintLinkedCount,
  getResourceCounts,
  loadSiteFormSubmissions,
  loadSiteBlueprints,
  loadSiteResources,
  type SiteFormSubmissionItem,
  type SiteFormSubmissionPageContext,
} from '@/lib/dashboardSiteCenter';
import {
  buildSiteResourceContent,
  buildSiteResourceDraft,
  createSiteResourceCode,
  createSiteResourceDraft,
  getSiteResourceDisplayLink,
  getSiteResourceDisplayNote,
  getSiteResourceDisplayTitle,
  parseSiteResource,
} from '@/lib/siteResource';
import type { CreationCampaignBlueprint } from '@/types/creation';
import type { SiteResource } from '@/types/site';

type SiteCenterTab = 'resources' | 'campaign' | 'submissions';
type ResourceFilter = 'all' | 'image' | 'pdf' | 'url';

type CampaignDeliveryItem = {
  meta: string;
  metric: string;
  status: string;
  title: string;
};

type CampaignDeliveryGroup = {
  icon: string;
  items: CampaignDeliveryItem[];
  title: string;
};

const SITE_CENTER_DIALOG_STYLES = `
[data-dashboard-site-center] {
  --border: rgba(0,0,0,0.07);
  --violet: #6D28D9;
  --cyan: #0891B2;
  --text: #0D0D1A;
  --text3: rgba(13,13,26,0.38);
  --success: #059669;
  --grad: linear-gradient(135deg, #6D28D9, #0891B2);
  --site-center-safe-vh: var(--cove-safe-vh, 100vh);
}

[data-dashboard-site-center] *,
[data-dashboard-site-center] *::before,
[data-dashboard-site-center] *::after {
  box-sizing: border-box;
}

[data-dashboard-site-center] .resource-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(13,13,26,.42);
  z-index: 220;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 20px;
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
}

[data-dashboard-site-center] .resource-modal-backdrop.open {
  display: flex;
}

[data-dashboard-site-center] .resource-modal {
  width: min(1040px, 100%);
  height: min(calc(var(--site-center-safe-vh) - 40px), 860px);
  max-height: min(calc(var(--site-center-safe-vh) - 40px), 860px);
  background: #fff;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 22px;
  box-shadow: 0 26px 60px rgba(15,23,42,.20);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: Inter, sans-serif;
  color: var(--text);
}

[data-dashboard-site-center] .resource-modal-head {
  padding: 18px 20px 16px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  background:
    radial-gradient(circle at top right, rgba(14,165,233,.12), transparent 42%),
    linear-gradient(180deg, rgba(239,246,255,.86), rgba(255,255,255,.98));
}

[data-dashboard-site-center] .resource-modal-eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--violet);
  margin-bottom: 7px;
}

[data-dashboard-site-center] .resource-modal-title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -.4px;
  color: var(--text);
}

[data-dashboard-site-center] .resource-modal-sub {
  margin-top: 5px;
  font-size: 12.5px;
  line-height: 1.65;
  color: rgba(15,23,42,.68);
  max-width: 640px;
}

[data-dashboard-site-center] .resource-modal-close,
[data-dashboard-site-center] .site-center-tab,
[data-dashboard-site-center] .resource-chip,
[data-dashboard-site-center] .cfg-save-btn,
[data-dashboard-site-center] .resource-row,
[data-dashboard-site-center] .campaign-blueprint,
[data-dashboard-site-center] .campaign-back-btn {
  appearance: none;
  text-decoration: none;
}

[data-dashboard-site-center] .resource-modal-close {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,.94);
  color: var(--text3);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all .2s;
  flex-shrink: 0;
}

[data-dashboard-site-center] .resource-modal-close:hover {
  border-color: rgba(37,99,235,.28);
  background: rgba(37,99,235,.06);
  color: var(--violet);
}

[data-dashboard-site-center] .resource-modal-body {
  padding: 16px;
  background: rgba(241,245,251,.84);
  overflow: hidden;
  flex: 1;
  min-height: 0;
  height: 0;
}

[data-dashboard-site-center] .resource-shell {
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  min-height: 0;
}

[data-dashboard-site-center] .resource-pane {
  height: 100%;
  min-height: 0;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 26px rgba(15,23,42,.05);
}

[data-dashboard-site-center] .site-center-tabs {
  margin: 12px 14px 0;
  padding: 4px;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  background: rgba(248,250,252,.92);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.82);
}

[data-dashboard-site-center] .site-center-tab {
  min-width: 0;
  height: 34px;
  padding: 0 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgba(15,23,42,.58);
  font-size: 12px;
  font-weight: 800;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  white-space: nowrap;
  transition: background .18s, color .18s, box-shadow .18s;
}

[data-dashboard-site-center] .site-center-tab i {
  flex-shrink: 0;
  font-size: 12px;
}

[data-dashboard-site-center] .site-center-tab.active {
  background: #fff;
  color: var(--violet);
  box-shadow: 0 1px 4px rgba(15,23,42,.10);
}

[data-dashboard-site-center] .site-center-panel {
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  display: none;
  flex-direction: column;
}

[data-dashboard-site-center] .site-center-panel.active {
  display: flex;
}

[data-dashboard-site-center] .resource-toolbar {
  padding: 14px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

[data-dashboard-site-center] .resource-pane-title,
[data-dashboard-site-center] .rm-pane-title {
  font-size: 14px;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -.2px;
}

[data-dashboard-site-center] .resource-pane-copy,
[data-dashboard-site-center] .rm-pane-copy {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(15,23,42,.64);
  line-height: 1.55;
}

[data-dashboard-site-center] .resource-filter {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

[data-dashboard-site-center] .resource-chip {
  border: 1px solid rgba(15,23,42,.08);
  background: #fff;
  color: rgba(15,23,42,.68);
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  font-family: inherit;
  transition: all .18s;
}

[data-dashboard-site-center] .resource-chip.on {
  border-color: rgba(37,99,235,.22);
  color: var(--violet);
  background: rgba(37,99,235,.06);
}

[data-dashboard-site-center] .resource-summary {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  background: rgba(248,250,252,.9);
}

[data-dashboard-site-center] .resource-stat {
  padding: 11px 12px;
  border-radius: 13px;
  border: 1px solid rgba(15,23,42,.06);
  background: #fff;
  min-width: 0;
}

[data-dashboard-site-center] .resource-stat-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: rgba(15,23,42,.46);
  margin-bottom: 6px;
}

[data-dashboard-site-center] .resource-stat-value {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -.03em;
  color: var(--text);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

[data-dashboard-site-center] .resource-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

[data-dashboard-site-center] .resource-row {
  width: 100%;
  padding: 13px 14px;
  border-radius: 16px;
  border: 1px solid rgba(15,23,42,.08);
  background: linear-gradient(180deg, #fff, rgba(248,250,252,.92));
  text-align: left;
  font-family: inherit;
  cursor: pointer;
}

[data-dashboard-site-center] .resource-row-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

[data-dashboard-site-center] .resource-row-copy {
  min-width: 0;
  flex: 1;
}

[data-dashboard-site-center] .resource-row-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 5px;
}

[data-dashboard-site-center] .resource-kind,
[data-dashboard-site-center] .rm-kind {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 999px;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .03em;
  border: 1px solid rgba(37,99,235,.14);
  background: rgba(37,99,235,.08);
  color: var(--violet);
}

[data-dashboard-site-center] .resource-subtype,
[data-dashboard-site-center] .resource-badge,
[data-dashboard-site-center] .rm-subtype {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 999px;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .03em;
  border: 1px solid rgba(15,23,42,.08);
  background: rgba(248,250,252,.88);
  color: rgba(15,23,42,.60);
}

[data-dashboard-site-center] .resource-badge {
  background: #fff;
  color: rgba(15,23,42,.54);
}

[data-dashboard-site-center] .resource-row-title {
  font-size: 13px;
  font-weight: 800;
  line-height: 1.45;
  color: var(--text);
}

[data-dashboard-site-center] .resource-row-link {
  font-size: 11px;
  color: rgba(15,23,42,.62);
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .resource-row-note {
  margin-top: 8px;
  font-size: 11.5px;
  color: rgba(15,23,42,.58);
  line-height: 1.58;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

[data-dashboard-site-center] .cfg-save-btn {
  padding: 9px 22px;
  border-radius: 9px;
  border: none;
  background: var(--grad);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: opacity .2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  white-space: nowrap;
}

[data-dashboard-site-center] .cfg-save-btn:hover {
  opacity: .9;
}

[data-dashboard-site-center] .cfg-save-btn:disabled,
[data-dashboard-site-center] .ads-btn:disabled,
[data-dashboard-site-center] .auth-secondary:disabled {
  opacity: .56;
  cursor: not-allowed;
}

[data-dashboard-site-center] .ads-btn,
[data-dashboard-site-center] .auth-secondary {
  min-height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 700;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: pointer;
  transition: all .18s;
}

[data-dashboard-site-center] .ads-btn {
  border: none;
  background: var(--grad);
  color: #fff;
}

[data-dashboard-site-center] .ads-btn:hover:not(:disabled) {
  opacity: .92;
}

[data-dashboard-site-center] .auth-secondary {
  border: 1px solid rgba(15,23,42,.10);
  background: #fff;
  color: rgba(15,23,42,.72);
}

[data-dashboard-site-center] .auth-secondary:hover:not(:disabled) {
  border-color: rgba(37,99,235,.22);
  background: rgba(37,99,235,.05);
  color: var(--violet);
}

[data-dashboard-site-center] .resource-editor-panel {
  overflow: hidden;
}

[data-dashboard-site-center] .rm-editor {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

[data-dashboard-site-center] .rm-editor-head {
  padding: 16px 18px 14px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: linear-gradient(180deg, rgba(248,250,252,.94), #fff);
}

[data-dashboard-site-center] .rm-editor-copy {
  min-width: 0;
  flex: 1;
}

[data-dashboard-site-center] .rm-kicker {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--violet);
  margin-bottom: 7px;
}

[data-dashboard-site-center] .rm-back {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(15,23,42,.08);
  background: #fff;
  color: rgba(15,23,42,.56);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all .18s;
}

[data-dashboard-site-center] .rm-back:hover {
  border-color: rgba(37,99,235,.22);
  background: rgba(37,99,235,.05);
  color: var(--violet);
}

[data-dashboard-site-center] .rm-editor-body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 18px;
  display: grid;
  gap: 14px;
}

[data-dashboard-site-center] .rm-card {
  min-width: 0;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(248,250,252,.94), #fff);
  padding: 14px;
}

[data-dashboard-site-center] .rm-label {
  font-size: 10px;
  font-weight: 800;
  color: rgba(15,23,42,.48);
  letter-spacing: .08em;
  text-transform: uppercase;
  margin-bottom: 9px;
}

[data-dashboard-site-center] .rm-preview {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,.08);
  background: #fff;
}

[data-dashboard-site-center] .rm-preview-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37,99,235,.08);
  color: var(--violet);
  font-size: 16px;
  border: 1px solid rgba(37,99,235,.16);
}

[data-dashboard-site-center] .rm-preview-copy {
  min-width: 0;
  flex: 1;
}

[data-dashboard-site-center] .rm-preview-title {
  font-size: 14px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.4;
}

[data-dashboard-site-center] .rm-note {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(15,23,42,.58);
  line-height: 1.58;
}

[data-dashboard-site-center] .rm-preview-meta {
  margin-top: 10px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

[data-dashboard-site-center] .rm-replace {
  display: grid;
  gap: 10px;
}

[data-dashboard-site-center] .rm-replace-box {
  padding: 12px;
  border-radius: 14px;
  border: 1px dashed rgba(109,40,217,.22);
  background: rgba(109,40,217,.04);
}

[data-dashboard-site-center] .rm-replace-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

[data-dashboard-site-center] .rm-replace-copy strong {
  display: block;
  font-size: 12px;
  color: var(--text);
  margin-bottom: 3px;
}

[data-dashboard-site-center] .rm-replace-copy span {
  font-size: 11px;
  color: rgba(15,23,42,.56);
  line-height: 1.55;
}

[data-dashboard-site-center] .rm-upload-note {
  font-size: 11px;
  color: rgba(15,23,42,.56);
  line-height: 1.5;
}

[data-dashboard-site-center] .rm-field-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

[data-dashboard-site-center] .rm-field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

[data-dashboard-site-center] .rm-field label {
  font-size: 12px;
  font-weight: 700;
  color: rgba(15,23,42,.72);
}

[data-dashboard-site-center] .rm-input,
[data-dashboard-site-center] .rm-select,
[data-dashboard-site-center] .rm-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 12px;
  background: #fff;
  color: var(--text);
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;
  padding: 11px 12px;
  outline: none;
  transition: border-color .18s, box-shadow .18s, background .18s;
}

[data-dashboard-site-center] .rm-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 36px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23999'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
}

[data-dashboard-site-center] .rm-input:focus,
[data-dashboard-site-center] .rm-select:focus,
[data-dashboard-site-center] .rm-textarea:focus {
  border-color: rgba(109,40,217,.32);
  box-shadow: 0 0 0 3px rgba(109,40,217,.10);
}

[data-dashboard-site-center] .rm-textarea {
  min-height: 110px;
  resize: vertical;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  overflow-x: hidden;
}

[data-dashboard-site-center] .rm-hint {
  margin-top: 6px;
  font-size: 11px;
  color: rgba(15,23,42,.52);
  line-height: 1.55;
}

[data-dashboard-site-center] .rm-editor-foot {
  padding: 14px 18px;
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
}

[data-dashboard-site-center] .rm-foot-note {
  font-size: 12px;
  color: rgba(15,23,42,.56);
  line-height: 1.55;
}

[data-dashboard-site-center] .rm-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

[data-dashboard-site-center] .rm-danger {
  border-color: rgba(220,38,38,.18);
  color: #b91c1c;
  background: rgba(220,38,38,.04);
}

[data-dashboard-site-center] .rm-danger:hover:not(:disabled) {
  border-color: rgba(220,38,38,.28);
  background: rgba(220,38,38,.08);
  color: #991b1b;
}

[data-dashboard-site-center] .resource-empty {
  margin: auto 0;
  padding: 24px 18px;
  border: 1px dashed rgba(15,23,42,.12);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(248,250,252,.88), #fff);
  text-align: center;
}

[data-dashboard-site-center] .resource-loading {
  display: grid;
  justify-items: center;
  gap: 12px;
}

[data-dashboard-site-center] .resource-loading-icon {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37,99,235,.08);
  border: 1px solid rgba(37,99,235,.12);
  color: var(--violet);
  font-size: 15px;
}

[data-dashboard-site-center] .resource-loading-icon i {
  animation: dashboard-site-center-spin 1s linear infinite;
}

[data-dashboard-site-center] .resource-empty-title {
  color: var(--text);
  font-size: 14px;
  font-weight: 800;
}

[data-dashboard-site-center] .resource-empty-copy {
  max-width: 520px;
  margin: 6px auto 0;
  color: rgba(15,23,42,.56);
  font-size: 12.5px;
  line-height: 1.6;
}

@keyframes dashboard-site-center-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

[data-dashboard-site-center] .submissions-panel {
  padding: 14px;
  gap: 12px;
}

[data-dashboard-site-center] .submissions-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 2px 2px 12px;
  border-bottom: 1px solid rgba(15,23,42,.08);
}

[data-dashboard-site-center] .submissions-title {
  font-size: 13px;
  font-weight: 850;
  letter-spacing: -.2px;
  color: var(--text);
  margin-bottom: 5px;
}

[data-dashboard-site-center] .submissions-copy {
  font-size: 12px;
  line-height: 1.6;
  color: rgba(15,23,42,.6);
}

[data-dashboard-site-center] .submissions-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

[data-dashboard-site-center] .submission-stat {
  min-width: 0;
  padding: 11px 12px;
  border-radius: 13px;
  border: 1px solid rgba(15,23,42,.06);
  background: #fff;
}

[data-dashboard-site-center] .submission-stat-label {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: rgba(15,23,42,.46);
  margin-bottom: 6px;
}

[data-dashboard-site-center] .submission-stat-value {
  min-width: 0;
  font-size: 17px;
  font-weight: 850;
  letter-spacing: -.03em;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .submission-list {
  display: grid;
  gap: 10px;
}

[data-dashboard-site-center] .submission-card {
  padding: 14px;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 15px;
  background: #fff;
}

[data-dashboard-site-center] .submission-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

[data-dashboard-site-center] .submission-page {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(37,99,235,.07);
  border: 1px solid rgba(37,99,235,.13);
  color: var(--violet);
  font-size: 10.5px;
  font-weight: 850;
  white-space: nowrap;
  min-width: 0;
}

[data-dashboard-site-center] .submission-page span {
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .submission-time {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 750;
  color: rgba(15,23,42,.48);
  white-space: nowrap;
}

[data-dashboard-site-center] .submission-person {
  display: block;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(15,23,42,.07);
}

[data-dashboard-site-center] .submission-name {
  font-size: 14px;
  font-weight: 850;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .submission-contact {
  margin-top: 4px;
  font-size: 11.5px;
  color: rgba(15,23,42,.58);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .submission-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
  margin-top: 12px;
}

[data-dashboard-site-center] .submission-field {
  min-width: 0;
  padding: 9px 10px;
  border-radius: 11px;
  background: rgba(248,250,252,.9);
  border: 1px solid rgba(15,23,42,.06);
}

[data-dashboard-site-center] .submission-field-label {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: rgba(15,23,42,.42);
  margin-bottom: 5px;
}

[data-dashboard-site-center] .submission-field-value {
  font-size: 11.5px;
  font-weight: 750;
  line-height: 1.42;
  color: rgba(15,23,42,.72);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .campaign-panel {
  padding: 14px;
  gap: 12px;
}

[data-dashboard-site-center] .campaign-stack {
  display: grid;
  gap: 10px;
}

[data-dashboard-site-center] .campaign-intro {
  padding: 2px 2px 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(15,23,42,.08);
}

[data-dashboard-site-center] .campaign-intro-title {
  font-size: 13px;
  font-weight: 850;
  letter-spacing: -.2px;
  color: var(--text);
  margin-bottom: 5px;
}

[data-dashboard-site-center] .campaign-intro-copy {
  font-size: 12px;
  line-height: 1.6;
  color: rgba(15,23,42,.6);
}

[data-dashboard-site-center] .campaign-intro-copy strong {
  color: var(--text);
  font-weight: 700;
}

[data-dashboard-site-center] .campaign-blueprint-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}

[data-dashboard-site-center] .campaign-blueprint {
  width: 100%;
  display: grid;
  gap: 9px;
  padding: 14px;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 14px;
  background: #fff;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color .18s, background .18s;
}

[data-dashboard-site-center] .campaign-blueprint:hover {
  border-color: rgba(37,99,235,.20);
  background: rgba(248,250,252,.62);
}

[data-dashboard-site-center] .campaign-blueprint-body {
  display: grid;
  gap: 12px;
  min-width: 0;
}

[data-dashboard-site-center] .campaign-blueprint-summary {
  display: grid;
  gap: 8px;
}

[data-dashboard-site-center] .campaign-blueprint-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

[data-dashboard-site-center] .campaign-open-indicator {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37,99,235,.07);
  color: var(--violet);
  flex-shrink: 0;
}

[data-dashboard-site-center] .campaign-detail-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(15,23,42,.08);
}

[data-dashboard-site-center] .campaign-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 9px;
  background: #fff;
  color: rgba(15,23,42,.68);
  font-size: 11.5px;
  font-weight: 800;
  font-family: inherit;
  cursor: pointer;
}

[data-dashboard-site-center] .campaign-hero {
  padding: 2px 2px 12px;
  border-bottom: 1px solid rgba(15,23,42,.08);
  background: transparent;
}

[data-dashboard-site-center] .campaign-hero-top,
[data-dashboard-site-center] .campaign-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

[data-dashboard-site-center] .campaign-kicker {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: rgba(15,23,42,.48);
  margin-bottom: 6px;
}

[data-dashboard-site-center] .campaign-title {
  font-size: 15px;
  font-weight: 850;
  letter-spacing: -.25px;
  color: var(--text);
}

[data-dashboard-site-center] .campaign-status {
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(5,150,105,.09);
  border: 1px solid rgba(5,150,105,.16);
  color: var(--success);
  font-size: 10.5px;
  font-weight: 800;
  white-space: nowrap;
}

[data-dashboard-site-center] .campaign-copy {
  margin-top: 9px;
  font-size: 12.5px;
  line-height: 1.65;
  color: rgba(15,23,42,.66);
}

[data-dashboard-site-center] .campaign-metrics {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 12px;
}

[data-dashboard-site-center] .campaign-metric {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  border-radius: 999px;
  background: rgba(248,250,252,.9);
  border: 1px solid rgba(15,23,42,.06);
  padding: 6px 9px;
}

[data-dashboard-site-center] .campaign-metric-label {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: rgba(15,23,42,.44);
}

[data-dashboard-site-center] .campaign-metric-value {
  font-size: 11px;
  font-weight: 850;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .campaign-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  border-top: 1px solid rgba(15,23,42,.08);
}

[data-dashboard-site-center] .campaign-delivery {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  border-top: 1px solid rgba(15,23,42,.08);
}

[data-dashboard-site-center] .campaign-delivery-group {
  border-bottom: 1px solid rgba(15,23,42,.08);
  background: transparent;
  padding: 12px 2px;
}

[data-dashboard-site-center] .campaign-delivery-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

[data-dashboard-site-center] .campaign-delivery-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 850;
  color: var(--text);
}

[data-dashboard-site-center] .campaign-delivery-title i {
  color: var(--violet);
  font-size: 12px;
}

[data-dashboard-site-center] .campaign-delivery-count {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid rgba(15,23,42,.08);
  color: rgba(15,23,42,.54);
  font-size: 10.5px;
  font-weight: 850;
}

[data-dashboard-site-center] .campaign-delivery-list {
  display: grid;
  gap: 0;
}

[data-dashboard-site-center] .campaign-delivery-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid rgba(15,23,42,.06);
  background: transparent;
}

[data-dashboard-site-center] .campaign-delivery-copy {
  min-width: 0;
}

[data-dashboard-site-center] .campaign-delivery-name {
  font-size: 12px;
  font-weight: 850;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .campaign-delivery-meta {
  margin-top: 4px;
  font-size: 11px;
  color: rgba(15,23,42,.56);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-dashboard-site-center] .campaign-delivery-side {
  display: grid;
  justify-items: end;
  gap: 4px;
  font-size: 11px;
  font-weight: 750;
  color: rgba(15,23,42,.58);
  white-space: nowrap;
}

[data-dashboard-site-center] .campaign-delivery-status {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(37,99,235,.07);
  border: 1px solid rgba(37,99,235,.13);
  color: var(--violet);
  font-size: 10px;
  font-weight: 850;
}

[data-dashboard-site-center] .campaign-section {
  border: none;
  border-bottom: 1px solid rgba(15,23,42,.08);
  border-radius: 0;
  background: transparent;
  padding: 12px 2px;
}

[data-dashboard-site-center] .campaign-section-title {
  font-size: 13px;
  font-weight: 850;
  color: var(--text);
}

[data-dashboard-site-center] .campaign-section-icon {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37,99,235,.08);
  color: var(--violet);
  flex-shrink: 0;
}

[data-dashboard-site-center] .campaign-list {
  margin-top: 8px;
  display: grid;
  gap: 5px;
}

[data-dashboard-site-center] .campaign-list-item {
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 10px;
  font-size: 11.5px;
  line-height: 1.45;
  color: rgba(15,23,42,.66);
}

[data-dashboard-site-center] .campaign-list-item strong {
  color: rgba(15,23,42,.76);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
}

[data-dashboard-site-center] .campaign-actions {
  padding-top: 12px;
}

@media (max-width: 960px) {
  [data-dashboard-site-center] .resource-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  [data-dashboard-site-center] .resource-modal-backdrop {
    padding: 10px;
  }

  [data-dashboard-site-center] .resource-modal {
    height: calc(var(--site-center-safe-vh) - 20px);
    max-height: calc(var(--site-center-safe-vh) - 20px);
    border-radius: 18px;
  }

  [data-dashboard-site-center] .resource-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  [data-dashboard-site-center] .cfg-save-btn {
    width: 100%;
  }

  [data-dashboard-site-center] .rm-field-grid {
    grid-template-columns: 1fr;
  }

  [data-dashboard-site-center] .rm-replace-top,
  [data-dashboard-site-center] .rm-editor-foot {
    flex-direction: column;
    align-items: stretch;
  }

  [data-dashboard-site-center] .rm-actions > * {
    width: 100%;
  }

  [data-dashboard-site-center] .resource-summary {
    grid-template-columns: 1fr;
  }

  [data-dashboard-site-center] .submissions-summary,
  [data-dashboard-site-center] .submission-fields {
    grid-template-columns: 1fr;
  }

  [data-dashboard-site-center] .submission-head {
    flex-direction: column;
  }

  [data-dashboard-site-center] .campaign-list-item,
  [data-dashboard-site-center] .campaign-delivery-item {
    grid-template-columns: 1fr;
  }

  [data-dashboard-site-center] .campaign-delivery-side {
    justify-items: start;
  }
}
`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const normalizeText = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : '';

const getSectionIcon = (title: string) => {
  const normalized = title.toLowerCase();
  if (normalized.includes('audience')) return 'fa-bullseye';
  if (normalized.includes('promotion') || normalized.includes('message')) return 'fa-message';
  if (normalized.includes('conversion') || normalized.includes('path')) return 'fa-route';
  if (normalized.includes('measure') || normalized.includes('metric')) return 'fa-chart-line';
  if (normalized.includes('setup')) return 'fa-layer-group';
  return 'fa-compass';
};

const getResourceKindMeta = (resource: SiteResource) => {
  const parsed = parseSiteResource(resource);

  if (parsed.type === 'pdf' || parsed.type === 'file') {
    return {
      filter: 'pdf' as const,
      icon: 'fa-file-pdf',
      label: parsed.type === 'pdf' ? 'PDF' : 'File',
      subtype: 'Asset',
    };
  }

  if (parsed.type === 'url' || parsed.type === 'offer') {
    return {
      filter: 'url' as const,
      icon: 'fa-link',
      label: 'URL',
      subtype: 'Reference',
    };
  }

  return {
    filter: 'image' as const,
    icon: 'fa-image',
    label: 'Image',
    subtype: 'Asset',
  };
};

const getBlueprintMetrics = (blueprint: CreationCampaignBlueprint) => blueprint.metrics.slice(0, 3);

const parseBlueprintSectionItems = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const dividerIndex = line.indexOf(':');
      if (dividerIndex <= 0) {
        return { label: 'Detail', value: line };
      }

      return {
        label: line.slice(0, dividerIndex).trim(),
        value: line.slice(dividerIndex + 1).trim(),
      };
    });

const getRecordField = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (key in record) return record[key];
  }
  return undefined;
};

const parseCampaignDeliveryItems = (
  value: unknown,
  options: {
    fallbackMetric: string;
    metaKeys: string[];
    metricKeys: string[];
    statusKeys: string[];
    titleKeys: string[];
  },
) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item, index): CampaignDeliveryItem | null => {
      if (typeof item === 'string' && item.trim()) {
        return {
          meta: '',
          metric: options.fallbackMetric,
          status: 'Linked',
          title: item.trim(),
        };
      }

      if (!isRecord(item)) return null;

      const title =
        normalizeText(getRecordField(item, options.titleKeys)) || `Linked item ${index + 1}`;
      const meta = normalizeText(getRecordField(item, options.metaKeys));
      const status = normalizeText(getRecordField(item, options.statusKeys)) || 'Linked';
      const metric =
        normalizeText(getRecordField(item, options.metricKeys)) || options.fallbackMetric;

      return {
        meta,
        metric,
        status,
        title,
      };
    })
    .filter((item): item is CampaignDeliveryItem => item !== null);
};

const getCampaignDeliveryGroups = (
  blueprint: CreationCampaignBlueprint,
): CampaignDeliveryGroup[] => {
  if (!isRecord(blueprint.content)) return [];

  const content = blueprint.content as Record<string, unknown>;
  const landingPages = parseCampaignDeliveryItems(
    getRecordField(content, ['landingPages', 'landing_pages', 'linkedLandingPages']),
    {
      fallbackMetric: `${blueprint.linkedLandingPageCount || 0} linked`,
      metaKeys: ['url', 'path', 'slug', 'link'],
      metricKeys: ['metric', 'summary', 'statusLabel'],
      statusKeys: ['status', 'state'],
      titleKeys: ['title', 'name', 'label'],
    },
  );
  const adCampaigns = parseCampaignDeliveryItems(
    getRecordField(content, [
      'adCampaigns',
      'ad_campaigns',
      'googleAdsCampaigns',
      'google_ads_campaigns',
    ]),
    {
      fallbackMetric: `${blueprint.linkedAdCampaignCount || 0} linked`,
      metaKeys: ['channel', 'platform', 'url', 'link'],
      metricKeys: ['metric', 'summary', 'statusLabel'],
      statusKeys: ['status', 'state'],
      titleKeys: ['title', 'name', 'label'],
    },
  );

  return [
    {
      icon: 'fa-window-maximize',
      items: landingPages,
      title: 'Landing pages',
    },
    {
      icon: 'fa-rectangle-ad',
      items: adCampaigns,
      title: 'Google Ads campaigns',
    },
  ].filter((group) => group.items.length > 0);
};

interface DashboardSiteCenterDialogProps {
  campaignEnabled?: boolean;
  onClose: () => void;
  onOpenAddPage?: (preferredBlueprintId?: string | null) => void;
  open: boolean;
  siteDomain: string;
  siteId: number | null;
  submissionInstanceToken?: string;
  submissionPageContexts?: SiteFormSubmissionPageContext[];
  submissionTenantIds?: number[];
}

const renderEmptyState = (title: string, copy: string) => (
  <div className="resource-empty">
    <div className="resource-empty-title">{title}</div>
    <div className="resource-empty-copy">{copy}</div>
  </div>
);

const renderLoadingState = (title: string, copy: string) => (
  <div className="resource-empty resource-loading" aria-live="polite">
    <div className="resource-loading-icon" aria-hidden="true">
      <i className="fa-solid fa-spinner" />
    </div>
    <div className="resource-empty-title">{title}</div>
    <div className="resource-empty-copy">{copy}</div>
  </div>
);

export const DashboardSiteCenterDialog = ({
  campaignEnabled = true,
  onClose,
  onOpenAddPage,
  open,
  siteDomain,
  siteId,
  submissionInstanceToken,
  submissionPageContexts,
  submissionTenantIds,
}: DashboardSiteCenterDialogProps) => {
  const [activeTab, setActiveTab] = useState<SiteCenterTab>('resources');
  const [filter, setFilter] = useState<ResourceFilter>('all');
  const [resources, setResources] = useState<SiteResource[]>([]);
  const [hasLoadedResources, setHasLoadedResources] = useState(false);
  const [resourcesError, setResourcesError] = useState('');
  const [resourceDraft, setResourceDraft] = useState<ResourceDraft | null>(null);
  const [resourceSaving, setResourceSaving] = useState(false);
  const [resourceDeleting, setResourceDeleting] = useState(false);
  const [blueprints, setBlueprints] = useState<CreationCampaignBlueprint[]>([]);
  const [hasLoadedBlueprints, setHasLoadedBlueprints] = useState(false);
  const [blueprintsError, setBlueprintsError] = useState('');
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<SiteFormSubmissionItem[]>([]);
  const [submissionTotal, setSubmissionTotal] = useState(0);
  const [hasLoadedSubmissions, setHasLoadedSubmissions] = useState(false);
  const [submissionsError, setSubmissionsError] = useState('');
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const safeSiteDomain = siteDomain.trim() || 'this site';
  const resourceCounts = useMemo(() => getResourceCounts(resources), [resources]);
  const resourceFilterItems = useMemo(
    () => [
      { key: 'all' as const, label: 'All', count: hasLoadedResources ? resources.length : '--' },
      {
        key: 'image' as const,
        label: 'Images',
        count: hasLoadedResources ? resourceCounts.image : '--',
      },
      { key: 'pdf' as const, label: 'PDFs', count: hasLoadedResources ? resourceCounts.pdf : '--' },
      { key: 'url' as const, label: 'URLs', count: hasLoadedResources ? resourceCounts.url : '--' },
    ],
    [
      hasLoadedResources,
      resourceCounts.image,
      resourceCounts.pdf,
      resourceCounts.url,
      resources.length,
    ],
  );

  const filteredResources = useMemo(() => {
    if (filter === 'all') return resources;
    return resources.filter((resource) => getResourceKindMeta(resource).filter === filter);
  }, [filter, resources]);

  const selectedBlueprint =
    blueprints.find(
      (item) => item.localId === selectedBlueprintId || String(item.id) === selectedBlueprintId,
    ) || null;

  const selectedBlueprintIndex = selectedBlueprint
    ? blueprints.findIndex(
        (item) =>
          item.localId === selectedBlueprint.localId || String(item.id) === selectedBlueprintId,
      )
    : -1;

  const selectedBlueprintDeliveryGroups = useMemo(
    () => (selectedBlueprint ? getCampaignDeliveryGroups(selectedBlueprint) : []),
    [selectedBlueprint],
  );
  const validSubmissionTenantIds = useMemo(
    () => [
      ...new Set(
        (submissionTenantIds || [])
          .map(Number)
          .filter((tenantId) => Number.isFinite(tenantId) && tenantId > 0),
      ),
    ],
    [submissionTenantIds],
  );
  const hasSubmissionQuery = Boolean(submissionInstanceToken || validSubmissionTenantIds.length);
  const submissionPageCount = useMemo(
    () => new Set(submissions.map((item) => item.page).filter(Boolean)).size,
    [submissions],
  );

  useEffect(() => {
    if (open) return;
    setActiveTab('resources');
    setFilter('all');
    setResourceDraft(null);
    setResourceSaving(false);
    setResourceDeleting(false);
    setSelectedBlueprintId(null);
    setResources([]);
    setHasLoadedResources(false);
    setResourcesError('');
    setBlueprints([]);
    setHasLoadedBlueprints(false);
    setBlueprintsError('');
    setSubmissions([]);
    setSubmissionTotal(0);
    setHasLoadedSubmissions(false);
    setSubmissionsError('');
  }, [open]);

  useEffect(() => {
    if (campaignEnabled) return;
    setActiveTab('resources');
    setSelectedBlueprintId(null);
  }, [campaignEnabled]);

  useEffect(() => {
    if (!open) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (resourceDraft) {
        setResourceDraft(null);
        return;
      }
      if (selectedBlueprintId) {
        setSelectedBlueprintId(null);
        return;
      }
      onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open, resourceDraft, selectedBlueprintId]);

  useEffect(() => {
    if (!open) return;

    if (!siteId) {
      setResources([]);
      setHasLoadedResources(false);
      setResourcesError('');
      return;
    }

    let cancelled = false;

    const loadResourcesForSite = async () => {
      setResources([]);
      setHasLoadedResources(false);
      setResourcesError('');

      try {
        const nextResources = await loadSiteResources(siteId);
        if (!cancelled) {
          setResources(nextResources);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setResources([]);
          setResourcesError(
            error instanceof Error ? error.message : 'Failed to load site resources.',
          );
        }
      } finally {
        if (!cancelled) {
          setHasLoadedResources(true);
        }
      }
    };

    void loadResourcesForSite();

    return () => {
      cancelled = true;
    };
  }, [open, siteId]);

  useEffect(() => {
    if (!open || !campaignEnabled) return;

    setSelectedBlueprintId(null);

    if (!siteId) {
      setBlueprints([]);
      setHasLoadedBlueprints(false);
      setBlueprintsError('');
      return;
    }

    let cancelled = false;

    const loadBlueprintsForSite = async () => {
      setBlueprints([]);
      setHasLoadedBlueprints(false);
      setBlueprintsError('');

      try {
        const nextBlueprints = await loadSiteBlueprints(siteId);
        if (!cancelled) {
          setBlueprints(nextBlueprints);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setBlueprints([]);
          setBlueprintsError(
            error instanceof Error ? error.message : 'Failed to load site campaign blueprints.',
          );
        }
      } finally {
        if (!cancelled) {
          setHasLoadedBlueprints(true);
        }
      }
    };

    void loadBlueprintsForSite();

    return () => {
      cancelled = true;
    };
  }, [campaignEnabled, open, siteId]);

  useEffect(() => {
    if (!open || !campaignEnabled || activeTab !== 'submissions' || hasLoadedSubmissions) return;

    if (!hasSubmissionQuery) {
      setSubmissions([]);
      setSubmissionTotal(0);
      setSubmissionsError('');
      setHasLoadedSubmissions(true);
      return;
    }

    let cancelled = false;

    const loadSubmissionsForSite = async () => {
      setSubmissions([]);
      setSubmissionTotal(0);
      setHasLoadedSubmissions(false);
      setSubmissionsError('');

      try {
        const response = await loadSiteFormSubmissions({
          instanceToken: submissionInstanceToken,
          pageContexts: submissionPageContexts,
          tenantIds: validSubmissionTenantIds,
        });

        if (!cancelled) {
          setSubmissions(response.submissions);
          setSubmissionTotal(response.total);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setSubmissions([]);
          setSubmissionTotal(0);
          setSubmissionsError(
            error instanceof Error ? error.message : 'Failed to load form submissions.',
          );
        }
      } finally {
        if (!cancelled) {
          setHasLoadedSubmissions(true);
        }
      }
    };

    void loadSubmissionsForSite();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    campaignEnabled,
    hasLoadedSubmissions,
    hasSubmissionQuery,
    open,
    submissionInstanceToken,
    submissionPageContexts,
    validSubmissionTenantIds,
  ]);

  const reloadResources = async () => {
    if (!siteId) return [];
    const nextResources = await loadSiteResources(siteId);
    setResources(nextResources);
    setResourcesError('');
    setHasLoadedResources(true);
    return nextResources;
  };

  const openCreateResource = () => {
    setActiveTab('resources');
    setSelectedBlueprintId(null);
    setResourceDraft(normalizeDraftToAssetEditor(createSiteResourceDraft() as ResourceDraft));
  };

  const openEditResource = (resource: SiteResource) => {
    setActiveTab('resources');
    setSelectedBlueprintId(null);
    setResourceDraft(
      normalizeDraftToAssetEditor(buildSiteResourceDraft(resource) as ResourceDraft),
    );
  };

  const closeResourceEditor = () => {
    setResourceDraft(null);
  };

  const updateDraft = (next: Partial<ResourceDraft>) => {
    setResourceDraft((previous) => (previous ? { ...previous, ...next } : previous));
  };

  const handleSubtypeChange = (subtype: string) => {
    setResourceDraft((previous) => {
      if (!previous) return previous;
      const normalizedSubtype =
        normalizeSubtype(subtype) || getDefaultAssetSubtype(getAssetEditorKind(previous));
      return normalizeDraftToAssetEditor({
        ...previous,
        resourceType: 'asset',
        subtype: normalizedSubtype,
        resourceCode: createSiteResourceCode('asset', normalizedSubtype, previous.resourceCode),
        title: '',
        locator: '',
        note: '',
        phone: '',
        email: '',
        address: '',
        question: '',
        answer: '',
        body: '',
        description: '',
        mimeType: '',
        fileSize: undefined,
        coverUrl: '',
        objectName: undefined,
        sourceType: 'manual',
        storageType: '',
        target: '',
      });
    });
  };

  const handleAssetKindChange = (kind: AssetEditorKind) => {
    setResourceDraft((previous) => {
      if (!previous) return previous;
      const nextSubtype = getDefaultAssetSubtype(kind);
      return normalizeDraftToAssetEditor({
        ...previous,
        resourceType: 'asset',
        subtype: nextSubtype,
        resourceCode: createSiteResourceCode('asset', nextSubtype, previous.resourceCode),
        title: '',
        locator: '',
        note: '',
        phone: '',
        email: '',
        address: '',
        question: '',
        answer: '',
        body: '',
        description: '',
        mimeType: '',
        fileSize: undefined,
        coverUrl: '',
        objectName: undefined,
        sourceType: 'manual',
        storageType: '',
        target: '',
      });
    });
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !siteId || !resourceDraft || resourceDraft.resourceType !== 'asset') return;

    const uploadKind = getAssetTransportKind(resourceDraft.subtype, file.type, file.name);
    if (uploadKind === 'url') return;

    try {
      setResourceSaving(true);
      const init = await siteApi.initResourceUpload({
        siteId,
        filename: file.name,
        contentType: file.type || undefined,
        resourceType: 'asset',
        resourceSubtype: uploadKind,
      });

      const response = await fetch(init.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }

      const nextDraft: ResourceDraft = {
        ...resourceDraft,
        title: resourceDraft.title.trim() || file.name.replace(/\.[^.]+$/, ''),
        locator: init.viewUrl,
        objectName: init.objectName,
        mimeType: file.type || resourceDraft.mimeType,
        fileSize: file.size,
      };
      const resourceCode = buildDraftResourceCode(nextDraft);
      const contentSchema = getDraftSchemaName(
        nextDraft.resourceType,
        nextDraft.subtype,
        nextDraft.mimeType,
        init.viewUrl,
      );
      const contentJson = JSON.stringify(buildSiteResourceContent(nextDraft, init.viewUrl));
      const primaryText = getDraftPrimaryText(nextDraft);

      const saved = await siteApi.saveResource({
        id: nextDraft.id,
        siteId,
        resourceType: nextDraft.resourceType,
        resourceSubtype: nextDraft.subtype,
        resourceCode,
        status: 'active',
        defaultFlag: 0,
        sourceType: 'upload',
        storageType: 'oss',
        contentSchema,
        schemaVersion: 1,
        primaryText,
        primaryUrl: init.viewUrl,
        mimeType: file.type || undefined,
        fileSize: file.size,
        coverUrl: uploadKind === 'image' ? init.viewUrl : nextDraft.coverUrl || undefined,
        contentJson,
        metaJson: JSON.stringify({
          note: nextDraft.note.trim(),
          originalFilename: file.name,
          resourceType: nextDraft.resourceType,
          subtype: nextDraft.subtype,
        }),
      });

      await reloadResources();
      setResourceDraft(normalizeDraftToAssetEditor(buildSiteResourceDraft(saved) as ResourceDraft));
      setFilter((previous) => (previous === 'all' ? previous : getDraftFilterKind(nextDraft)));
      toast.success(`Replacement file uploaded: ${file.name}`);
    } catch (uploadError) {
      toast.error(getErrorMessage(uploadError, 'Resource upload failed.'));
    } finally {
      setResourceSaving(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!resourceDraft || !siteId) return;

    const validationError = getDraftValidationError(resourceDraft);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const locator = resourceDraft.locator.trim();
    const resourceCode = buildDraftResourceCode(resourceDraft);
    const contentSchema = getDraftSchemaName(
      resourceDraft.resourceType,
      resourceDraft.subtype,
      resourceDraft.mimeType,
      locator,
    );
    const contentJson = JSON.stringify(buildSiteResourceContent(resourceDraft, locator));
    const primaryText = getDraftPrimaryText(resourceDraft);
    const assetKind =
      resourceDraft.resourceType === 'asset'
        ? getAssetTransportKind(resourceDraft.subtype, resourceDraft.mimeType, locator)
        : null;

    try {
      setResourceSaving(true);
      await siteApi.saveResource({
        id: resourceDraft.id,
        siteId,
        resourceType: resourceDraft.resourceType,
        resourceSubtype: resourceDraft.subtype,
        resourceCode,
        status: 'active',
        defaultFlag: 0,
        sourceType: resourceDraft.sourceType || 'manual',
        storageType: resourceDraft.storageType || inferStorageType(resourceDraft),
        contentSchema,
        schemaVersion: 1,
        primaryText,
        primaryUrl: locator || undefined,
        coverUrl:
          resourceDraft.resourceType === 'asset' && assetKind === 'image'
            ? resourceDraft.coverUrl || locator || undefined
            : resourceDraft.coverUrl || undefined,
        mimeType: resourceDraft.mimeType || undefined,
        fileSize: resourceDraft.fileSize || undefined,
        contentJson,
        metaJson: JSON.stringify({
          note: resourceDraft.note.trim(),
          resourceType: resourceDraft.resourceType,
          subtype: resourceDraft.subtype,
        }),
      });

      await reloadResources();
      setFilter((previous) => (previous === 'all' ? previous : getDraftFilterKind(resourceDraft)));
      closeResourceEditor();
      toast.success(
        resourceDraft.id ? 'Resource changes saved' : 'Resource added to the current site library',
      );
    } catch (saveError) {
      toast.error(getErrorMessage(saveError, 'Failed to save resource.'));
    } finally {
      setResourceSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!resourceDraft?.id || !siteId) return;

    try {
      setResourceDeleting(true);
      await siteApi.deleteResource({
        siteId,
        id: resourceDraft.id,
      });
      await reloadResources();
      closeResourceEditor();
      toast.success(`${getDraftPrimaryText(resourceDraft)} removed from site resources`);
    } catch (removeError) {
      toast.error(getErrorMessage(removeError, 'Failed to delete resource.'));
    } finally {
      setResourceDeleting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div data-dashboard-site-center>
      <style>{SITE_CENTER_DIALOG_STYLES}</style>

      <div
        className="resource-modal-backdrop open"
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          onClose();
        }}
      >
        <div
          className="resource-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resourceModalTitle"
        >
          <div className="resource-modal-head">
            <div>
              <div className="resource-modal-eyebrow">
                {campaignEnabled ? 'Site center' : 'Site resources'}
              </div>
              <div className="resource-modal-title" id="resourceModalTitle">
                {campaignEnabled ? `${safeSiteDomain} site center` : 'Resource manager'}
              </div>
              <div className="resource-modal-sub">
                {campaignEnabled
                  ? `Manage resources, campaign planning, form submissions, and historical promotion performance for ${safeSiteDomain}.`
                  : 'Manage images, PDFs, and URLs available to Studio for the current page.'}
              </div>
            </div>

            <button
              className="resource-modal-close"
              type="button"
              onClick={onClose}
              aria-label="Close resource manager"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          <div className="resource-modal-body">
            <div className="resource-shell">
              <div className="resource-pane" id="resourceListPane">
                {campaignEnabled && !resourceDraft ? (
                  <div className="site-center-tabs" role="tablist" aria-label="Site center">
                    <button
                      className={
                        activeTab === 'resources' ? 'site-center-tab active' : 'site-center-tab'
                      }
                      type="button"
                      role="tab"
                      aria-selected={activeTab === 'resources'}
                      data-site-tab="resources"
                      onClick={() => {
                        setActiveTab('resources');
                        setSelectedBlueprintId(null);
                      }}
                    >
                      <i className="fa-solid fa-folder-open" />
                      Resources
                    </button>
                    <button
                      className={
                        activeTab === 'campaign' ? 'site-center-tab active' : 'site-center-tab'
                      }
                      type="button"
                      role="tab"
                      aria-selected={activeTab === 'campaign'}
                      data-site-tab="campaign"
                      onClick={() => setActiveTab('campaign')}
                    >
                      <i className="fa-solid fa-bullhorn" />
                      Campaign Blueprint
                    </button>
                    <button
                      className={
                        activeTab === 'submissions' ? 'site-center-tab active' : 'site-center-tab'
                      }
                      type="button"
                      role="tab"
                      aria-selected={activeTab === 'submissions'}
                      data-site-tab="submissions"
                      onClick={() => {
                        setActiveTab('submissions');
                        setSelectedBlueprintId(null);
                      }}
                    >
                      <i className="fa-solid fa-inbox" />
                      Submissions
                    </button>
                  </div>
                ) : null}

                <div
                  className={
                    activeTab === 'resources'
                      ? resourceDraft
                        ? 'site-center-panel active resource-editor-panel'
                        : 'site-center-panel active'
                      : 'site-center-panel'
                  }
                  id="sitePanel-resources"
                >
                  {resourceDraft ? (
                    <AutopilotResourceEditorPane
                      deleting={resourceDeleting}
                      draft={resourceDraft}
                      onAssetKindChange={handleAssetKindChange}
                      onBack={closeResourceEditor}
                      onDraftChange={updateDraft}
                      onRemove={handleRemove}
                      onSave={handleSave}
                      onSubtypeChange={handleSubtypeChange}
                      onUpload={handleUpload}
                      saving={resourceSaving}
                      siteDomain={safeSiteDomain}
                      siteId={siteId}
                      uploadInputRef={uploadInputRef}
                    />
                  ) : (
                    <>
                      <div className="resource-toolbar">
                        <div>
                          <div className="resource-pane-title">Current site library</div>
                          <div className="resource-pane-copy">
                            Studio can reuse these resources in page sections, metadata, and launch
                            setup.
                          </div>
                        </div>

                        <button
                          className="cfg-save-btn"
                          type="button"
                          onClick={openCreateResource}
                          disabled={!siteId}
                        >
                          <i className="fa-solid fa-plus" />
                          Add resource
                        </button>
                      </div>

                      <div className="resource-filter">
                        {resourceFilterItems.map((item) => (
                          <button
                            key={item.key}
                            className={filter === item.key ? 'resource-chip on' : 'resource-chip'}
                            type="button"
                            onClick={() => setFilter(item.key)}
                          >
                            {item.label} {item.count}
                          </button>
                        ))}
                      </div>

                      <div className="resource-summary">
                        <div className="resource-stat">
                          <div className="resource-stat-label">Site</div>
                          <div className="resource-stat-value">{safeSiteDomain}</div>
                        </div>
                        <div className="resource-stat">
                          <div className="resource-stat-label">Images</div>
                          <div className="resource-stat-value">
                            {!hasLoadedResources ? '--' : resourceCounts.image}
                          </div>
                        </div>
                        <div className="resource-stat">
                          <div className="resource-stat-label">PDFs</div>
                          <div className="resource-stat-value">
                            {!hasLoadedResources ? '--' : resourceCounts.pdf}
                          </div>
                        </div>
                        <div className="resource-stat">
                          <div className="resource-stat-label">URLs</div>
                          <div className="resource-stat-value">
                            {!hasLoadedResources ? '--' : resourceCounts.url}
                          </div>
                        </div>
                      </div>

                      <div className="resource-list">
                        {!siteId
                          ? renderEmptyState(
                              'No site selected',
                              'This domain group does not have a valid site connection yet.',
                            )
                          : !hasLoadedResources
                            ? renderLoadingState(
                                'Loading resources',
                                'Pulling the current site library from the API.',
                              )
                            : resourcesError
                              ? renderEmptyState('Failed to load resources', resourcesError)
                              : filteredResources.length
                                ? filteredResources.map((resource) => {
                                    const meta = getResourceKindMeta(resource);
                                    const title = getSiteResourceDisplayTitle(resource);

                                    return (
                                      <button
                                        key={resource.id}
                                        className="resource-row"
                                        type="button"
                                        aria-label={`Edit ${title}`}
                                        onClick={() => openEditResource(resource)}
                                      >
                                        <div className="resource-row-head">
                                          <div className="resource-row-copy">
                                            <div className="resource-row-meta">
                                              <span className="resource-kind">
                                                <i className={`fa-solid ${meta.icon}`} />
                                                {meta.label}
                                              </span>
                                              <span className="resource-subtype">
                                                {meta.subtype}
                                              </span>
                                            </div>
                                            <div className="resource-row-title">{title}</div>
                                          </div>
                                          <span className="resource-badge">
                                            <i className="fa-solid fa-pen" />
                                            Edit
                                          </span>
                                        </div>
                                        <div className="resource-row-link">
                                          {getSiteResourceDisplayLink(resource)}
                                        </div>
                                        <div className="resource-row-note">
                                          {getSiteResourceDisplayNote(resource) ||
                                            'Available to Studio for this site.'}
                                        </div>
                                      </button>
                                    );
                                  })
                                : renderEmptyState(
                                    'No resources yet',
                                    'Add reusable images, PDFs, or URLs for this site.',
                                  )}
                      </div>
                    </>
                  )}
                </div>

                {campaignEnabled ? (
                  <div
                    className={
                      activeTab === 'campaign'
                        ? 'site-center-panel campaign-panel active'
                        : 'site-center-panel campaign-panel'
                    }
                    id="sitePanel-campaign"
                  >
                    {!selectedBlueprint ? (
                      <>
                        <div className="campaign-intro">
                          <div className="campaign-intro-title">Campaign blueprints</div>
                          <div className="campaign-intro-copy">
                            Strategic directions your expert built from this site&apos;s resources
                            and campaign history. Open one to review its full plan, or start a new
                            page from a blueprint via <strong>Add page</strong>.
                          </div>
                        </div>

                        <div className="campaign-stack">
                          {!siteId
                            ? renderEmptyState(
                                'No site selected',
                                'This domain group does not have a valid site connection yet.',
                              )
                            : !hasLoadedBlueprints
                              ? renderLoadingState(
                                  'Loading blueprints',
                                  'Pulling the current site campaign directions from the API.',
                                )
                              : blueprintsError
                                ? renderEmptyState('Failed to load blueprints', blueprintsError)
                                : blueprints.length
                                  ? blueprints.map((blueprint, index) => (
                                      <button
                                        key={blueprint.localId}
                                        className="campaign-blueprint"
                                        type="button"
                                        onClick={() => setSelectedBlueprintId(blueprint.localId)}
                                      >
                                        <div className="campaign-blueprint-summary">
                                          <div className="campaign-blueprint-meta">
                                            <div>
                                              <div className="campaign-kicker">
                                                Blueprint {String(index + 1).padStart(2, '0')} /{' '}
                                                {getBlueprintLinkedCount(blueprint)} linked items
                                              </div>
                                              <div className="campaign-title">
                                                {blueprint.title}
                                              </div>
                                            </div>
                                            <span className="campaign-open-indicator">
                                              <i className="fa-solid fa-chevron-right" />
                                            </span>
                                          </div>
                                          <div className="campaign-copy">{blueprint.summary}</div>
                                          <div className="campaign-blueprint-foot">
                                            <span className="campaign-status">
                                              {blueprint.status || 'Ready'}
                                            </span>
                                            <div className="campaign-metrics">
                                              {getBlueprintMetrics(blueprint).map((metric) => (
                                                <span
                                                  key={`${blueprint.localId}-${metric.label}`}
                                                  className="campaign-metric"
                                                >
                                                  <span className="campaign-metric-label">
                                                    {metric.label}
                                                  </span>
                                                  <span className="campaign-metric-value">
                                                    {metric.value}
                                                  </span>
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    ))
                                  : renderEmptyState(
                                      'No blueprints yet',
                                      'Use Add page to generate the first campaign direction for this site.',
                                    )}
                        </div>
                      </>
                    ) : (
                      <div className="campaign-blueprint-body">
                        <div className="campaign-detail-nav">
                          <button
                            className="campaign-back-btn"
                            type="button"
                            onClick={() => setSelectedBlueprintId(null)}
                          >
                            <i className="fa-solid fa-arrow-left" />
                            Back to blueprints
                          </button>
                          <span className="campaign-status">
                            {selectedBlueprint.status || 'Ready'}
                          </span>
                        </div>

                        <div className="campaign-hero">
                          <div className="campaign-hero-top">
                            <div>
                              <div className="campaign-kicker">
                                Plan{' '}
                                {String(
                                  (selectedBlueprintIndex >= 0 ? selectedBlueprintIndex : 0) + 1,
                                ).padStart(2, '0')}{' '}
                                / {getBlueprintLinkedCount(selectedBlueprint)} linked items
                              </div>
                              <div className="campaign-title">{selectedBlueprint.title}</div>
                            </div>
                          </div>
                          <div className="campaign-copy">{selectedBlueprint.summary}</div>
                          <div className="campaign-metrics">
                            {selectedBlueprint.metrics.length ? (
                              selectedBlueprint.metrics.map((metric) => (
                                <div
                                  key={`${selectedBlueprint.localId}-${metric.label}`}
                                  className="campaign-metric"
                                >
                                  <div className="campaign-metric-label">{metric.label}</div>
                                  <div className="campaign-metric-value">{metric.value}</div>
                                </div>
                              ))
                            ) : (
                              <div className="campaign-metric">
                                <div className="campaign-metric-label">Status</div>
                                <div className="campaign-metric-value">
                                  {selectedBlueprint.status || 'Ready'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {selectedBlueprint.content.sections?.length ? (
                          <div className="campaign-grid">
                            {selectedBlueprint.content.sections.map((section) => (
                              <section
                                key={`${selectedBlueprint.localId}-${section.title}`}
                                className="campaign-section"
                              >
                                <div className="campaign-section-head">
                                  <div className="campaign-section-title">{section.title}</div>
                                  <div className="campaign-section-icon">
                                    <i className={`fa-solid ${getSectionIcon(section.title)}`} />
                                  </div>
                                </div>
                                <div className="campaign-list">
                                  {parseBlueprintSectionItems(section.value).map(
                                    (item, itemIndex) => (
                                      <div
                                        key={`${section.title}-${item.label}-${itemIndex}`}
                                        className="campaign-list-item"
                                      >
                                        <strong>{item.label}</strong>
                                        <span>{item.value}</span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </section>
                            ))}
                          </div>
                        ) : (
                          renderEmptyState(
                            'No detailed sections available',
                            'This blueprint is stored as a summary only.',
                          )
                        )}

                        {selectedBlueprintDeliveryGroups.length ? (
                          <div className="campaign-delivery">
                            {selectedBlueprintDeliveryGroups.map((group) => (
                              <section key={group.title} className="campaign-delivery-group">
                                <div className="campaign-delivery-head">
                                  <div className="campaign-delivery-title">
                                    <i className={`fa-solid ${group.icon}`} />
                                    {group.title}
                                  </div>
                                  <span className="campaign-delivery-count">
                                    {group.items.length}
                                  </span>
                                </div>
                                <div className="campaign-delivery-list">
                                  {group.items.map((item, index) => (
                                    <div
                                      key={`${group.title}-${item.title}-${index}`}
                                      className="campaign-delivery-item"
                                    >
                                      <div className="campaign-delivery-copy">
                                        <div className="campaign-delivery-name">{item.title}</div>
                                        <div className="campaign-delivery-meta">{item.meta}</div>
                                      </div>
                                      <div className="campaign-delivery-side">
                                        <span className="campaign-delivery-status">
                                          {item.status}
                                        </span>
                                        <span>{item.metric}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </section>
                            ))}
                          </div>
                        ) : null}

                        <div className="campaign-actions">
                          <button
                            className="cfg-save-btn"
                            type="button"
                            onClick={() => onOpenAddPage?.(selectedBlueprint.localId)}
                          >
                            <i className="fa-solid fa-plus" />
                            Add page from this blueprint
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {campaignEnabled ? (
                  <div
                    className={
                      activeTab === 'submissions'
                        ? 'site-center-panel submissions-panel active'
                        : 'site-center-panel submissions-panel'
                    }
                    id="sitePanel-submissions"
                  >
                    <div className="submissions-toolbar">
                      <div>
                        <div className="submissions-title">Form submission data</div>
                        <div className="submissions-copy">Review submissions from this site.</div>
                      </div>
                    </div>

                    <div className="submissions-summary">
                      <div className="submission-stat">
                        <div className="submission-stat-label">Total</div>
                        <div className="submission-stat-value">
                          {!hasLoadedSubmissions ? '--' : submissionTotal || submissions.length}
                        </div>
                      </div>
                      <div className="submission-stat">
                        <div className="submission-stat-label">Pages</div>
                        <div className="submission-stat-value">
                          {!hasLoadedSubmissions ? '--' : submissionPageCount}
                        </div>
                      </div>
                    </div>

                    {!siteId ? (
                      renderEmptyState(
                        'No site selected',
                        'This domain group does not have a valid site connection yet.',
                      )
                    ) : !hasSubmissionQuery ? (
                      renderEmptyState(
                        'No submission channels',
                        'This site does not have tenant IDs or an instance token available for form submission lookup.',
                      )
                    ) : !hasLoadedSubmissions ? (
                      renderLoadingState(
                        'Loading submissions',
                        'Pulling captured form submission data from the API.',
                      )
                    ) : submissionsError ? (
                      renderEmptyState('Failed to load submissions', submissionsError)
                    ) : submissions.length ? (
                      <div className="submission-list">
                        {submissions.map((submission) => (
                          <article key={submission.id} className="submission-card">
                            <div className="submission-head">
                              <div className="submission-page">
                                <i className="fa-solid fa-file-lines" />
                                <span>
                                  {submission.page} / {submission.path}
                                </span>
                              </div>
                              <div className="submission-time">{submission.time}</div>
                            </div>

                            <div className="submission-person">
                              <div className="submission-name">{submission.title}</div>
                              <div className="submission-contact">{submission.contact}</div>
                            </div>

                            <div className="submission-fields">
                              <div className="submission-field">
                                <div className="submission-field-label">Submitted from</div>
                                <div className="submission-field-value">{submission.page}</div>
                              </div>
                              <div className="submission-field">
                                <div className="submission-field-label">Form</div>
                                <div className="submission-field-value">{submission.form}</div>
                              </div>
                              <div className="submission-field">
                                <div className="submission-field-label">Source</div>
                                <div className="submission-field-value">{submission.source}</div>
                              </div>
                              {submission.fields.map((field, fieldIndex) => (
                                <div
                                  key={`${submission.id}-${field.key}-${fieldIndex}`}
                                  className="submission-field"
                                >
                                  <div className="submission-field-label">{field.label}</div>
                                  <div className="submission-field-value">{field.value}</div>
                                </div>
                              ))}
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      renderEmptyState(
                        'No form submissions yet',
                        'No form submissions captured for this site yet.',
                      )
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DashboardSiteCenterDialog;
